import { Particle } from '../ParticleLayer'

const CONFIG = {
    behavior: 0.21,
    rotation: 126,
    entanglement: 100,
    density: 300, // Reduced from 800 for less dense lightning
    height: 800,
    speed: 15,
    radius: 60, // Increased from 10 to widen the vortex
    coreColorDef: "#2bff00",
    coreColorAtk: "#ff0000",
    glowColor: "#ffffff",
    spriteAtk: '/assets/effects/protector/ProtectorAttack.png',
    spriteDef: '/assets/effects/protector/ProtectorDefense.png'
}

const hexToRgb = (hex: string) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return r ? { r: parseInt(r[1], 16), g: parseInt(r[2], 16), b: parseInt(r[3], 16) } : { r: 255, g: 255, b: 255 }
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

class ProtectorParticle {
    history: { x: number, y: number, z: number }[]
    y: number
    strandId: number
    offset: number
    x: number = 0
    z: number = 0

    constructor(y?: number) {
        this.history = []
        this.y = y !== undefined ? y : (Math.random() - 0.5) * CONFIG.height * 2
        this.strandId = Math.floor(Math.random() * 8)
        this.offset = Math.random() * 1000
    }

    update(t: number) {
        this.y -= CONFIG.speed
        if (this.y < -CONFIG.height) {
            this.y = CONFIG.height
            this.history = []
        }

        const sa = (this.strandId / 8) * Math.PI * 2
        let bx = Math.cos(sa + this.y * 0.001) * CONFIG.radius
        let bz = Math.sin(sa + this.y * 0.001) * CONFIG.radius

        const ent = Math.sin(this.y * 0.005 + t * 0.5 + this.offset) * CONFIG.entanglement
        bx += Math.cos(sa + 1.57) * ent
        bz += Math.sin(sa + 1.57) * ent

        const sx = Math.sin(this.y * 0.015 + t + this.offset) * 40
        const sz = Math.cos(this.y * 0.015 + t + this.offset) * 40

        const step = 60
        const seed = Math.floor(this.y / step) + Math.floor(t * 12) * 137 + this.strandId
        const r1 = Math.sin(seed) * 43758.54
        const r2 = Math.cos(seed) * 43758.54
        const lx = (r1 - Math.floor(r1) - 0.5) * 120
        const lz = (r2 - Math.floor(r2) - 0.5) * 120

        this.x = bx + lerp(sx, lx, CONFIG.behavior)
        this.z = bz + lerp(sz, lz, CONFIG.behavior)

        this.history.push({ x: this.x, y: this.y, z: this.z })
        if (this.history.length > 15) this.history.shift()
    }

    draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, globalAlpha: number, isAttack: boolean) {
        const len = this.history.length
        if (len < 2) return

        const pitch = ((CONFIG.rotation - 90) * 1.5) * (Math.PI / 180)

        const p = (x: number, y: number, z: number) => {
            const ry = y * Math.cos(pitch) - z * Math.sin(pitch)
            const rz = y * Math.sin(pitch) + z * Math.cos(pitch)
            const s = 1000 / (1000 + rz + 800)
            return { x: cx + x * s, y: cy + ry * s, s }
        }

        const head = this.history[len - 1]
        const tail = this.history[0]
        const hp = p(head.x, head.y, head.z)
        const tp = p(tail.x, tail.y, tail.z)

        const w = lerp(8, 2, CONFIG.behavior) * hp.s
        const a = lerp(0.15, 0.9, CONFIG.behavior) * globalAlpha
        const cc = hexToRgb(isAttack ? CONFIG.coreColorAtk : CONFIG.coreColorDef)

        const g = ctx.createLinearGradient(tp.x, tp.y, hp.x, hp.y)
        g.addColorStop(0, `rgba(${cc.r},${cc.g},${cc.b},0)`)
        g.addColorStop(1, `rgba(${cc.r},${cc.g},${cc.b},${a})`)

        ctx.strokeStyle = g
        ctx.lineWidth = w
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.beginPath()
        ctx.moveTo(tp.x, tp.y)
        for (let i = 1; i < len; i++) {
            const pp = p(this.history[i].x, this.history[i].y, this.history[i].z)
            ctx.lineTo(pp.x, pp.y)
        }
        ctx.stroke()

        if (CONFIG.behavior > 0.5) {
            ctx.fillStyle = `rgba(255, 255, 255, ${globalAlpha})`
            ctx.beginPath()
            ctx.arc(hp.x, hp.y, 1.5 * hp.s, 0, 6.28)
            ctx.fill()
        }
    }
}

const spriteCache: Record<string, HTMLImageElement> = {}
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image()
        img.src = src
        spriteCache[src] = img
    }
    return spriteCache[src]
}
getSprite(CONFIG.spriteAtk)
getSprite(CONFIG.spriteDef)

export type ProtectorData = {
    particles: ProtectorParticle[]
    initialized: boolean
}

export type ProtectorEffect = {
    kind: 'protector'
    t: number
    isAttack: boolean
    isOpponent: boolean
    data?: ProtectorData
}

export const createProtectorEffect = (isAttack: boolean, isOpponent: boolean = false): ProtectorEffect => {
    return {
        kind: 'protector',
        t: 0,
        isAttack,
        isOpponent
    }
}

export const renderProtectorEffect = (
    eff: ProtectorEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const spritePath = eff.isAttack ? CONFIG.spriteAtk : CONFIG.spriteDef
    const img = getSprite(spritePath)

    if (!eff.data) {
        const particles = []
        for (let i = 0; i < CONFIG.density; i++) {
            particles.push(new ProtectorParticle(-CONFIG.height + (i / CONFIG.density) * CONFIG.height * 2))
        }
        eff.data = {
            particles,
            initialized: true
        }
    }

    const d = eff.data
    const totalDuration = 3000 // 3 seconds
    if (eff.t > totalDuration) return false

    // Global opacity curve: fade in quickly, fade out at end
    let globalAlpha = 1.0
    if (eff.t < 300) {
        globalAlpha = eff.t / 300
    } else if (eff.t > totalDuration - 500) {
        globalAlpha = (totalDuration - eff.t) / 500
    }

    const cx = window.innerWidth / 2
    const cy = window.innerHeight / 2

    ctx.save()

    // 1. Draw Sacral Flow Particles
    ctx.globalCompositeOperation = 'lighter'
    const simTime = eff.t / 800

    for (const p of d.particles) {
        p.update(simTime)
        p.draw(ctx, cx, cy, globalAlpha, eff.isAttack)
    }

    ctx.restore()

    ctx.save()
    // 2. Draw Sprite
    if (img.complete && img.naturalWidth > 0) {
        // Top-to-center spawn animation (slide down)
        const slideDuration = 800 // Slide down over 800ms
        const slideProgress = Math.min(1, eff.t / slideDuration)
        // ease-out cubic
        const easedSlide = 1 - Math.pow(1 - slideProgress, 3)
        const startY = -400 // Start well above center
        const currentY = startY + (cy - startY) * easedSlide

        ctx.translate(cx, currentY)

        // Scale for opponent orientation
        if (eff.isOpponent) {
            ctx.scale(-1, 1) // Only flip X
        }

        // Breathing (pulsating) scale animation
        // Sin wave between 0.95 and 1.05
        const breatheCycle = eff.t * 0.003
        const breatheScale = 1.0 + Math.sin(breatheCycle) * 0.05

        // Determine scale based on screen size (avoid too big)
        // The sprites in config seem high-res (1.5mb png), so scale down significantly 
        const baseScale = 0.35
        const finalScale = baseScale * breatheScale
        const w = img.naturalWidth * finalScale
        const h = img.naturalHeight * finalScale

        // Adjust shadow offset just for aesthetic
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)'
        ctx.shadowBlur = 30
        ctx.shadowOffsetY = 15

        ctx.globalAlpha = globalAlpha
        // Keep standard composite
        ctx.drawImage(img, -w / 2, -h / 2, w, h)
    }
    ctx.restore()

    return true
}
